import React, { useEffect, useMemo } from 'react';
import { Info } from 'lucide-react';

const SUBJECT_SKILL_SETS = {
  maths: {
    config: [
      {
        key: 'numerical_reasoning',
        label: 'Numerical\nReasoning &\nAccuracy',
        description:
          'Understanding and applying number concepts, operations, fractions, decimals, and integers accurately.'
      },
      {
        key: 'logical_patterns',
        label: 'Logical &\nPattern-Based\nThinking',
        description: 'Identifying patterns, sequences, analogies, and solving logic-based puzzles.'
      },
      {
        key: 'problem_solving',
        label: 'Problem\nSolving &\nApplication',
        description: 'Applying mathematical concepts to word problems and real-life situations.'
      },
      {
        key: 'spatial_geometry',
        label: 'Spatial &\nGeometrical\nVisualization',
        description: 'Understanding shapes, symmetry, mensuration, graphs, and spatial relationships.'
      },
      {
        key: 'math_hots',
        label: 'Higher-Order\nMathematical\nThinking',
        description: 'Solving multi-step, reasoning-intensive achievers-level mathematical problems.'
      }
    ],
    keywords: {
      numerical_reasoning: [
        'numerical',
        'number',
        'arithmetic',
        'operations',
        'fraction',
        'decimal',
        'integer',
        'calculation',
        'accuracy'
      ],
      logical_patterns: [
        'logical',
        'reasoning',
        'pattern',
        'series',
        'analogy',
        'classification',
        'sequence',
        'odd one out',
        'puzzle'
      ],
      problem_solving: ['problem', 'solve', 'application', 'word problem', 'real-life', 'strategy'],
      spatial_geometry: ['spatial', 'geometry', 'shape', 'symmetry', 'mensuration', 'graph', 'visual', 'rotation'],
      math_hots: ['higher-order', 'multi-step', 'challenging', 'achievers', 'hots', 'advanced']
    },
    label: 'Mathematics'
  },
  science: {
    config: [
      {
        key: 'conceptual_science',
        label: 'Conceptual\nUnderstanding',
        description: 'Grasping core concepts in Physics, Chemistry, and Biology.'
      },
      {
        key: 'scientific_reasoning',
        label: 'Scientific\nReasoning &\nLogic',
        description: 'Using logic to explain phenomena, relationships, and classifications.'
      },
      {
        key: 'science_application',
        label: 'Application of\nScientific\nKnowledge',
        description: 'Applying scientific concepts to real-life and environmental situations.'
      },
      {
        key: 'observation_analysis',
        label: 'Observation &\nAnalytical\nSkills',
        description: 'Interpreting diagrams, processes, experiments, and data.'
      },
      {
        key: 'science_hots',
        label: 'Higher-Order\nScientific\nThinking',
        description: 'Answering inference-based, prediction-driven achievers questions.'
      }
    ],
    keywords: {
      conceptual_science: ['concept', 'definition', 'understanding', 'physics', 'chemistry', 'biology', 'science basics'],
      scientific_reasoning: [
        'scientific reasoning',
        'logic',
        'hypothesis',
        'inference',
        'cause',
        'effect',
        'classification'
      ],
      science_application: ['application', 'real life', 'environment', 'daily life', 'use of science'],
      observation_analysis: ['observation', 'diagram', 'process', 'experiment', 'data', 'analysis'],
      science_hots: ['higher-order', 'prediction', 'inference', 'achievers', 'hots', 'advanced']
    },
    label: 'Science'
  },
  english: {
    config: [
      {
        key: 'vocabulary',
        label: 'Vocabulary &\nWord Knowledge',
        description: 'Understanding meanings, synonyms, antonyms, idioms, and word usage.'
      },
      {
        key: 'grammar',
        label: 'Grammar &\nLanguage\nStructure',
        description: 'Applying grammatical rules, tenses, and sentence structure correctly.'
      },
      {
        key: 'reading_comprehension',
        label: 'Reading\nComprehension &\nInterpretation',
        description: 'Understanding, interpreting, and inferring meaning from texts.'
      },
      {
        key: 'verbal_reasoning',
        label: 'Verbal\nReasoning &\nUsage',
        description: 'Solving jumbled sentences, analogies, and context-based language questions.'
      },
      {
        key: 'english_hots',
        label: 'Situational\nThinking &\nHOTS',
        description: 'Expressing ideas clearly through situational responses and achievers-level questions.'
      }
    ],
    keywords: {
      vocabulary: ['vocabulary', 'synonym', 'antonym', 'idiom', 'word meaning', 'usage'],
      grammar: ['grammar', 'tense', 'sentence structure', 'parts of speech', 'punctuation'],
      reading_comprehension: ['reading', 'comprehension', 'passage', 'interpretation', 'inference'],
      verbal_reasoning: ['verbal reasoning', 'analogy', 'jumbled', 'context', 'odd one out', 'word relations'],
      english_hots: ['higher-order', 'situational', 'achievers', 'hots', 'expression', 'writing']
    },
    label: 'English'
  },
  computer: {
    config: [
      {
        key: 'computer_fundamentals',
        label: 'Computer\nFundamentals',
        description: 'Understanding basic hardware, software, memory, and devices.'
      },
      {
        key: 'algorithmic_thinking',
        label: 'Logical &\nAlgorithmic\nThinking',
        description: 'Following steps, flowcharts, and logical sequences.'
      },
      {
        key: 'digital_application',
        label: 'Application &\nDigital\nAwareness',
        description: 'Using computers effectively in real-life scenarios.'
      },
      {
        key: 'cyber_safety',
        label: 'Cyber Safety &\nDigital Ethics',
        description: 'Understanding safe, responsible, and ethical computer usage.'
      },
      {
        key: 'computer_hots',
        label: 'Analytical\n& HOTS',
        description: 'Solving reasoning-based and scenario-driven computer questions.'
      }
    ],
    keywords: {
      computer_fundamentals: ['hardware', 'software', 'memory', 'device', 'input', 'output'],
      algorithmic_thinking: ['algorithm', 'step', 'procedure', 'flowchart', 'sequence', 'logic'],
      digital_application: ['application', 'digital', 'internet', 'computer use', 'real life'],
      cyber_safety: ['cyber', 'safety', 'ethics', 'privacy', 'security', 'responsible'],
      computer_hots: ['higher-order', 'scenario', 'achievers', 'hots', 'analysis', 'problem solving']
    },
    label: 'Computer'
  },
  gk: {
    config: [
      {
        key: 'general_awareness',
        label: 'General\nAwareness & Recall',
        description: 'Knowledge of India, the world, science, sports, and culture.'
      },
      {
        key: 'current_affairs',
        label: 'Current\nAffairs\nAwareness',
        description: 'Awareness of recent national and international events.'
      },
      {
        key: 'environment_social',
        label: 'Eco-Social\nAwareness&\nSensitivity',
        description: 'Understanding sustainability, conservation, and social responsibility.'
      },
      {
        key: 'life_skills',
        label: 'Life Skills &\nDecision\nMaking',
        description: 'Applying values, empathy, teamwork, and safety awareness.'
      },
      {
        key: 'gk_hots',
        label: 'Analytical\nThinking',
        description: 'Connecting facts and applying reasoning to GK-based achievers questions.'
      }
    ],
    keywords: {
      general_awareness: ['general awareness', 'facts', 'knowledge', 'india', 'world', 'culture', 'sports'],
      current_affairs: ['current affairs', 'recent', 'news', 'national', 'international'],
      environment_social: ['environment', 'sustainability', 'conservation', 'social', 'responsibility'],
      life_skills: ['life skills', 'decision', 'values', 'empathy', 'teamwork', 'safety'],
      gk_hots: ['analytical', 'reasoning', 'achievers', 'hots', 'connection']
    },
    label: 'General Knowledge'
  },
  evs: {
    config: [
      {
        key: 'environment_awareness',
        label: 'Environment &\nNature\nAwareness',
        description: 'Understanding plants, animals, habitats, and conservation.'
      },
      {
        key: 'community_society',
        label: 'Community &\nSociety\nUnderstanding',
        description: 'Exploring family, culture, traditions, and social life.'
      },
      {
        key: 'health_hygiene',
        label: 'Health &\nHygiene\nAwareness',
        description: 'Learning about safety, nutrition, and healthy habits.'
      },
      {
        key: 'resources_sustainability',
        label: 'Resources &\nSustainability',
        description: 'Using water, energy, and resources responsibly.'
      },
      {
        key: 'evs_hots',
        label: 'Critical\nThinking &\nHOTS',
        description: 'Applying EVS concepts to real-life situations and analysis.'
      }
    ],
    keywords: {
      environment_awareness: ['environment', 'plants', 'animals', 'nature', 'habitat', 'conservation'],
      community_society: ['community', 'family', 'society', 'neighborhood', 'culture', 'tradition'],
      health_hygiene: ['health', 'hygiene', 'cleanliness', 'safety', 'nutrition'],
      resources_sustainability: ['resources', 'water', 'energy', 'recycle', 'sustainable'],
      evs_hots: ['critical', 'analysis', 'application', 'achievers', 'hots']
    },
    label: 'Environmental Studies'
  },
  social_studies: {
    config: [
      {
        key: 'history_civics',
        label: 'Historical &\nCivic Understanding',
        description: 'Understanding history, civics, governance, and institutions.'
      },
      {
        key: 'geography',
        label: 'Geographical\nAwareness &\nMapping',
        description: 'Understanding physical, political, and environmental geography.'
      },
      {
        key: 'social_economic',
        label: 'Social &\nEconomic\nReasoning',
        description: 'Understanding society, resources, development, and economy.'
      },
      {
        key: 'data_interpretation',
        label: 'Interpretation of\nData & Events',
        description: 'Analyzing maps, timelines, charts, and case-based information.'
      },
      {
        key: 'sst_hots',
        label: 'Critical\nThinking &\nHOTS',
        description: 'Applying cause–effect reasoning and analytical thinking to social issues.'
      }
    ],
    keywords: {
      history_civics: ['history', 'civics', 'governance', 'institution', 'constitution'],
      geography: ['geography', 'map', 'location', 'physical', 'political', 'environment'],
      social_economic: ['society', 'resources', 'development', 'economy', 'trade'],
      data_interpretation: ['timeline', 'chart', 'data', 'case', 'interpretation'],
      sst_hots: ['critical', 'cause', 'effect', 'analysis', 'achievers', 'hots']
    },
    label: 'Social Studies'
  },
  hindi: {
    config: [
      {
        key: 'hindi_vocabulary',
        label: 'शब्दावली एवं\nशब्दार्थ',
        description: 'पर्यायवाची, विलोम, मुहावरे एवं शब्द ज्ञान।'
      },
      {
        key: 'hindi_grammar',
        label: 'व्याकरण एवं\nभाषा संरचना',
        description: 'संज्ञा, क्रिया, काल, वाक्य रचना का सही प्रयोग।'
      },
      {
        key: 'hindi_comprehension',
        label: 'पाठ बोध',
        description: 'गद्यांश का अर्थ ग्रहण एवं भावार्थ समझना।'
      },
      {
        key: 'hindi_expression',
        label: 'भाषा प्रयोग एवं\nअभिव्यक्ति',
        description: 'सही शब्द चयन और वाक्य सुधार।'
      },
      {
        key: 'hindi_hots',
        label: 'उच्च स्तरीय\nभाषाई चिंतन',
        description: 'तर्क, विश्लेषण एवं अर्थ आधारित प्रश्न।'
      }
    ],
    keywords: {
      hindi_vocabulary: ['शब्द', 'पर्यायवाची', 'विलोम', 'मुहावरे', 'शब्दार्थ'],
      hindi_grammar: ['व्याकरण', 'संज्ञा', 'क्रिया', 'काल', 'वाक्य'],
      hindi_comprehension: ['पाठ', 'गद्यांश', 'अर्थ', 'भावार्थ', 'समझ'],
      hindi_expression: ['अभिव्यक्ति', 'भाषा', 'शब्द चयन', 'वाक्य सुधार'],
      hindi_hots: ['तर्क', 'विश्लेषण', 'उच्च स्तरीय', 'अर्थ आधारित']
    },
    label: 'Hindi'
  }
};

const normalizeSkillKey = (value) =>
  (value || '').toString().trim().toLowerCase().replace(/\s+/g, '').replace(/[^a-z]/g, '');

const getSkillKeyFromText = (text, skillKeywords) => {
  if (!text) return null;
  const lower = text.toLowerCase();
  for (const [skillKey, keywords] of Object.entries(skillKeywords)) {
    if (keywords.some((keyword) => lower.includes(keyword))) {
      return skillKey;
    }
  }
  return null;
};

const coerceScoreValue = (value) => {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'object') {
    const direct = value?.score ?? value?.value ?? value?.percentage;
    if (direct != null) {
      const score = Number(direct);
      return Number.isFinite(score) ? score : null;
    }
    const earned = Number(value?.earned ?? value?.marks_awarded ?? value?.score_awarded);
    const possible = Number(value?.possible ?? value?.max_marks ?? value?.max_score);
    if (Number.isFinite(earned) && Number.isFinite(possible) && possible > 0) {
      return (earned / possible) * 100;
    }
    return null;
  }
  const score = Number(value);
  return Number.isFinite(score) ? score : null;
};

const extractSkillScores = (evaluation) => {
  if (!evaluation) return null;
  const raw =
    evaluation.skill_wise_scores ||
    evaluation.skill_scores ||
    evaluation.skillwise_scores ||
    evaluation.skillScores;
  if (raw) {
    if (Array.isArray(raw)) {
      const mapped = raw.reduce((acc, entry) => {
        const key = normalizeSkillKey(entry?.skill || entry?.label || entry?.name);
        const score = coerceScoreValue(entry);
        if (key && score != null) {
          acc[key] = score;
        }
        return acc;
      }, {});
      return mapped;
    }
    if (typeof raw === 'object') {
      return Object.entries(raw).reduce((acc, [key, value]) => {
        const normalizedKey = normalizeSkillKey(key);
        const score = coerceScoreValue(value);
        if (normalizedKey && score != null) {
          acc[normalizedKey] = score;
        }
        return acc;
      }, {});
    }
  }
  return null;
};

const buildSkillScoresFromQuestions = (questionAnalysis, skillConfig, skillKeywords) => {
  if (!Array.isArray(questionAnalysis) || questionAnalysis.length === 0) return null;
  const totals = skillConfig.reduce((acc, skill) => {
    acc[skill.key] = { earned: 0, possible: 0 };
    return acc;
  }, {});

  questionAnalysis.forEach((qa) => {
    const descriptor = [qa?.skill, qa?.topic, qa?.concept, qa?.question_text]
      .filter(Boolean)
      .join(' ');
    const key = getSkillKeyFromText(descriptor, skillKeywords);
    if (!key) return;

    const earned = Number(qa?.marks_awarded ?? qa?.score ?? 0);
    const possible = Number(qa?.max_marks ?? qa?.max_score ?? qa?.marks ?? 0);
    if (!Number.isFinite(possible) || possible <= 0) return;

    totals[key].earned += Number.isFinite(earned) ? earned : 0;
    totals[key].possible += possible;
  });

  return totals;
};

const getSkillScoreValue = (scoreMap, key) => {
  if (!scoreMap) return null;
  const normalizedKey = normalizeSkillKey(key);
  const matchedEntry = Object.entries(scoreMap).find(([entryKey]) => normalizeSkillKey(entryKey) === normalizedKey);
  if (!matchedEntry) return null;
  const score = Number(matchedEntry[1]);
  return Number.isFinite(score) ? Math.round(score) : null;
};

const getPrimarySkillScoreValue = (scoreMap, key) => {
  if (!scoreMap) return null;
  const normalizedKey = normalizeSkillKey(key);
  const matchedEntry = Object.entries(scoreMap).find(([entryKey]) => normalizeSkillKey(entryKey).includes(normalizedKey));
  if (!matchedEntry) return null;
  const score = Number(matchedEntry[1]);
  return Number.isFinite(score) ? Math.round(score) : null;
};

const normalizeSubject = (value) =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z\s]/g, '')
    .replace(/\s+/g, ' ');

const getSubjectKey = (subject) => {
  if (!subject) return null;
  if (subject.includes('math')) return 'maths';
  if (subject.includes('english') || subject.includes('language')) return 'english';
  if (
    subject.includes('science') ||
    subject.includes('physics') ||
    subject.includes('chemistry') ||
    subject.includes('biology')
  ) {
    return 'science';
  }
  if (subject.includes('integrated science')) return 'science';
  if (subject.includes('evs') || subject.includes('environmental studies')) return 'evs';
  if (subject.includes('gk') || subject.includes('general knowledge') || subject.includes('general')) {
    return 'gk';
  }
  if (
    subject.includes('computer') ||
    subject.includes('computing') ||
    subject.includes('coding') ||
    subject.includes('ict') ||
    subject.includes('informatics')
  ) {
    return 'computer';
  }
  if (
    subject.includes('social studies') ||
    subject.includes('social science') ||
    subject.includes('sst') ||
    subject.includes('history') ||
    subject.includes('history & civics') ||
    subject.includes('civics') ||
    subject.includes('geography') ||
    subject.includes('economics') ||
    subject.includes('political science') ||
    subject.includes('commerce') ||
    subject.includes('global perspectives')
  ) {
    return 'social_studies';
  }
  if (subject.includes('hindi')) return 'hindi';
  return null;
};

const buildSkillSetLabel = (label, isOlympiad) => (isOlympiad ? `Olympiad • ${label}` : label);

const getSkillSetForEvaluation = (evaluation) => {
  const isOlympiad = Boolean(
    evaluation?._taskInfo?.is_olympiad ||
      evaluation?.is_olympiad ||
      evaluation?.task?.is_olympiad ||
      evaluation?.worksheet?.is_olympiad
  );

  const subject = normalizeSubject(
    evaluation?.subject ||
      evaluation?.task?.subject ||
      evaluation?._taskInfo?.subject ||
      evaluation?.worksheet?.subject
  );

  const subjectKey = getSubjectKey(subject);
  const skillSet = SUBJECT_SKILL_SETS[subjectKey] || SUBJECT_SKILL_SETS.gk;
  return { ...skillSet, label: buildSkillSetLabel(skillSet.label, isOlympiad) };
};

const SkillWiseScoreChart = ({ evaluation }) => {
  const skillSet = useMemo(() => getSkillSetForEvaluation(evaluation), [evaluation]);

  const skillData = useMemo(() => {
    const rawScores = extractSkillScores(evaluation);
    const questionTotals = buildSkillScoresFromQuestions(
      evaluation?.question_analysis || evaluation?.questions,
      skillSet.config,
      skillSet.keywords
    );

    return skillSet.config.map((skill) => {
      const rawScore = getSkillScoreValue(rawScores, skill.key) ?? getPrimarySkillScoreValue(rawScores, skill.key);
      if (rawScore != null) {
        return { ...skill, score: Math.max(0, Math.min(100, rawScore)) };
      }

      if (questionTotals?.[skill.key]?.possible > 0) {
        const { earned, possible } = questionTotals[skill.key];
        const score = Math.round((earned / possible) * 100);
        return { ...skill, score: Math.max(0, Math.min(100, score)) };
      }

      return { ...skill, score: null };
    });
  }, [evaluation, skillSet]);

  useEffect(() => {
    if (!evaluation) return;
    console.log('[SkillWiseScoreChart] Skill visualization score computed', {
      evaluationId: evaluation?._id ?? evaluation?.id,
      subject:
        evaluation?.subject ||
        evaluation?.task?.subject ||
        evaluation?._taskInfo?.subject ||
        evaluation?.worksheet?.subject,
      skillScores: skillData
    });
  }, [evaluation, skillData]);

  const hasScores = skillData.some((skill) => skill.score !== null);
  const skillTitleSuffix = skillSet?.label ? ` • ${skillSet.label}` : '';
  if (!hasScores) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm skill-wise-chart">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-lg font-semibold text-indigo-700">Skill-wise Score{skillTitleSuffix}</h3>
          <Info className="h-4 w-4 text-indigo-400" />
        </div>
        <p className="text-sm text-gray-500">Skill-wise scores will appear once enough skill data is available.</p>
      </div>
    );
  }

  const size = 350;
  const center = size / 2;
  const radius = 75;
  const labelRadius = 110;
  const padding = 12;
  const levels = [25, 50, 75, 100];
  const angleStep = (Math.PI * 2) / skillSet.config.length;

  const getPoint = (value, index, baseRadius = radius) => {
    const angle = angleStep * index - Math.PI / 2;
    const r = (value / 100) * baseRadius;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle)
    };
  };

  const polygonPoints = skillData
    .map((skill, index) => {
      const value = skill.score ?? 0;
      const point = getPoint(value, index);
      return `${point.x},${point.y}`;
    })
    .join(' ');

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-5">
        <h3 className="text-xl font-semibold text-indigo-700">Skill-wise Score{skillTitleSuffix}</h3>
        <Info className="h-5 w-5 text-indigo-400" />
      </div>
      <div className="flex justify-center overflow-visible text-gray-900 dark:text-slate-100">
        <svg
          width={size}
          height={size}
          viewBox={`${-padding} ${-padding} ${size + padding * 2} ${size + padding * 2}`}
        >
          {levels.map((level) => {
            const points = skillSet.config.map((_, index) => {
              const point = getPoint(level, index, radius);
              return `${point.x},${point.y}`;
            }).join(' ');
            return (
              <polygon
                key={level}
                points={points}
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.35"
                strokeDasharray="6 6"
                strokeWidth="1"
              />
            );
          })}

          {skillSet.config.map((_, index) => {
            const point = getPoint(100, index, radius);
            return (
              <line
                key={index}
                x1={center}
                y1={center}
                x2={point.x}
                y2={point.y}
                stroke="currentColor"
                strokeOpacity="0.35"
                strokeWidth="1"
                strokeDasharray="4 6"
              />
            );
          })}

          <polygon
            points={polygonPoints}
            fill="url(#skillGradient)"
            stroke="#F97316"
            strokeWidth="2"
          />

          <defs>
            <linearGradient id="skillGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#F472B6" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#F59E0B" stopOpacity="0.25" />
            </linearGradient>
          </defs>

          {skillData.map((skill, index) => {
            const value = skill.score ?? 0;
            const point = getPoint(value, index);
            return (
              <circle key={skill.key} cx={point.x} cy={point.y} r="4" fill="#F97316" />
            );
          })}

          {skillData.map((skill, index) => {
            const angle = angleStep * index - Math.PI / 2;
            const x = center + labelRadius * Math.cos(angle);
            const y = center + labelRadius * Math.sin(angle);
            const textAnchor = x < center - 10 ? 'end' : x > center + 10 ? 'start' : 'middle';
            const lines = skill.label.split('\n');
            return (
              <text
                key={`${skill.key}-label`}
                x={x}
                y={y}
                textAnchor={textAnchor}
                fill="currentColor"
                fontSize="13"
                fontWeight="600"
              >
                <tspan x={x} dy="-6">{skill.score ?? 0}</tspan>
                {lines.map((line, lineIndex) => (
                  <tspan key={line} x={x} dy={lineIndex === 0 ? '16' : '14'} fontWeight="500">
                    {line}
                  </tspan>
                ))}
              </text>
            );
          })}

          <text x={center} y={center + 18} textAnchor="middle" fill="currentColor" fillOpacity="0.6" fontSize="12">0</text>
          <text x={center} y={center - radius + 8} textAnchor="middle" fill="currentColor" fillOpacity="0.6" fontSize="12">100</text>
          <text x={center} y={center - radius * 0.75 + 8} textAnchor="middle" fill="currentColor" fillOpacity="0.6" fontSize="12">75</text>
          <text x={center} y={center - radius * 0.5 + 8} textAnchor="middle" fill="currentColor" fillOpacity="0.6" fontSize="12">50</text>
          <text x={center} y={center - radius * 0.25 + 8} textAnchor="middle" fill="currentColor" fillOpacity="0.6" fontSize="12">25</text>
        </svg>
      </div>
    </div>
  );
};

export default SkillWiseScoreChart;